import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthenticationModel } from 'src/app/authentication/authentication.model';

export const signupGuard: CanActivateFn = (route, state) => {
    const auth = inject(AuthService);

    return auth.isAuthenticated$.pipe(
        switchMap((isAuthenticated) => {
            if (isAuthenticated) {
                return of(true);
            }
            const url = state.url;
            const pattern = /^\/?signup\/?$/;

            return auth
                .loginWithRedirect({
                    appState: { target: url },
                    authorizationParams: {
                        screen_hint: pattern.test(url) ? 'signup' : 'login'
                    }
                })
                .pipe(map(() => false));
        })
    );
};

export const signupStatusGuard: CanActivateFn = (route, state) => {
    const router = inject(Router);
    const auth = inject(AuthService);
    const authenticationModel = inject(AuthenticationModel);

    return auth.idTokenClaims$.pipe(
        switchMap((idToken) => {
            if (!idToken?.email_verified) {
                const url = state.url;
                return url.includes('signup/')
                    ? of(router.parseUrl(`${url}/verify-email`))
                    : of(router.parseUrl('/signup/verify-email'));
            }

            if (!idToken?.org_id) {
                return authenticationModel.getAppUserOrganizations(idToken.sub).pipe(
                    switchMap((organizations) => {
                        // Sign the user in to the only organization they are a member of
                        if (organizations.length === 1) {
                            return auth
                                .loginWithRedirect({
                                    appState: { target: state.url },
                                    authorizationParams: {
                                        organization: organizations[0].id
                                    }
                                })
                                .pipe(map(() => false));
                        }

                        // Redirect user to the organization selection page
                        if (organizations.length > 1) {
                            return auth
                                .loginWithRedirect({
                                    appState: { target: state.url }
                                })
                                .pipe(map(() => false));
                        }

                        return of(router.parseUrl('/signup'));
                    })
                );
            }
            return of(true);
        })
    );
};

export const verifyEmailGuard: CanActivateFn = (route, state) => {
    const router = inject(Router);
    const auth = inject(AuthService);

    return auth.idTokenClaims$.pipe(
        map((idToken) => {
            if (!idToken?.email_verified) {
                return true;
            }
            return router.parseUrl('/');
        })
    );
};
